import React, { Component } from 'react';
import { css } from '@emotion/react';

import Image from '../atoms/Image';
import Caption from '../atoms/Caption';
import Header from '../atoms/Header';
import BodyText from '../atoms/BodyText';
import CallToAction from './CallToAction';
import Grid2CellUnevenSplit from '../atoms/Grid2CellUnevenSplit';
import {
  filterPhone,
  filterEmail,
} from '../../../utils/filterContactInformation';
import {
  generateEmailProps,
  generatePhoneProps,
} from '../../../utils/generateContactInfoProps';

import * as Breakpoints from '../../../constants/breakpoints';
import * as InternalPropTypes from '../../../constants/internal-types';
import {
  ProfileEnum,
  TypeProfileTypes,
  TypeSEOWeightTypes,
  fontColorEnum,
} from '../../../constants/cms-constants';
import ScreenSize from '../../../constants/screenSize';
import { SizesType } from '../../../types/ts/imgixQuery';
import SEOWeights from '../../../constants/cms/control-options/seo-weights';
import { getSEOWeight } from '../../../utils/SEOWeight';

const styles = css({
  '& .image': {
    marginTop: '20px',
    marginBottom: '20px',
    objectFit: 'scale-down',
    [Breakpoints.TabletUp.mq]: {
      height: 'auto',
      width: '100%',
    },
    [Breakpoints.Mobile.mq]: {
      maxHeight: '400px',
      width: 'auto',
    },
  },
  '& .header-text p': {
    marginBottom: 0,
  },
  '& .title-profile': {
    fontSize: '20px',
  },
  '& .body-text p': {
    marginBottom: 0,
  },
  '& .profile-title': {
    [Breakpoints.TabletUp.mq]: {
      marginTop: '44px',
    },
    [Breakpoints.Mobile.mq]: {
      marginTop: '34px',
    },
  },
  '& .profile-text': {
    marginTop: '32px',
  },
  '& .contact-title': {
    marginTop: '20px',
    [Breakpoints.Mobile.mq]: {
      marginTop: '14px',
    },
  },
  '& .contact-info': {
    [Breakpoints.TabletUp.mq]: {
      marginBottom: '20px',
    },
    [Breakpoints.Mobile.mq]: {
      marginTop: '34px',
    },
    marginTop: '50px',
  },
  '& .contact-container': {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginLeft: '30px',
    height: '100%',
    [Breakpoints.Mobile.mq]: {
      marginLeft: '0',
      paddingLeft: '0',
    },
  },
  '& .info-element': {
    marginTop: '5px',
  },
  '& .contact-element': {
    color: fontColorEnum.BLUE,
  },
  '& .name-text': {
    marginBottom: '10px',
  },
});

type ProfileProps = {
  // eslint-disable-next-line no-restricted-globals
  name?: string;
  jobTitle?: string;
  phone?: string;
  email?: string;
  backLink?: InternalPropTypes.Link;
  image?: InternalPropTypes.Image;
  type?: TypeProfileTypes;
  header?: InternalPropTypes.RichText;
  bodyText?: InternalPropTypes.RichText;
  seoWeight?: TypeSEOWeightTypes;
};

class Profile extends Component<ProfileProps> {
  static types = ProfileEnum;

  static defaultProps = {
    name: '',
    jobTitle: '',
    phone: '',
    email: '',
    backLink: null,
    image: null,
    type: this.types.PROFILE,
    header: null,
    bodyText: null,
    seoWeight: SEOWeights.P,
  };

  render() {
    const {
      name,
      jobTitle,
      phone,
      email,
      image,
      backLink,
      type,
      header,
      bodyText,
      seoWeight,
    } = this.props;

    const phoneProps = generatePhoneProps(filterPhone(phone));
    const emailProps = generateEmailProps(filterEmail(email));

    const formatPhoneNumber = (phoneNumber) =>
      phoneNumber.replace(/(\d{2})/g, '$1 ').trim();

    const formattedPhone = formatPhoneNumber(phone);

    const sizes: SizesType = {
      [ScreenSize.Mobile]: { w: '286', h: '400' },
      [ScreenSize.Tablet]: { w: '301', h: '422' },
      [ScreenSize.Desktop]: { w: '333', h: '467' },
    };

    return (
      <div css={styles}>
        <Grid2CellUnevenSplit
          leftCellCount={4}
          rightCellCount={type === Profile.types.PROFILE ? 7 : 8}
          snapAtBreakpoint={Breakpoints.MobileDown}
          onRenderCell1={() => (
            <div>
              {type === Profile.types.PROFILE ? (
                <>
                  <CallToAction
                    link={backLink}
                    type={CallToAction.types.LEFT_POINTING}
                  />

                  <Image image={image} sizes={sizes} />

                  <Header type={Header.types.PARTNER}>{name}</Header>

                  <Caption type={Caption.types.TITLEPROFILE}>
                    {jobTitle}
                  </Caption>

                  <BodyText type={BodyText.types.STATIC16} {...emailProps}>
                    {email}
                  </BodyText>

                  <BodyText type={BodyText.types.STATIC16} {...phoneProps}>
                    {formattedPhone}
                  </BodyText>
                </>
              ) : (
                <Image image={image} sizes={sizes} />
              )}
            </div>
          )}
          onRenderCell2={() => (
            <div className={`${type}-container`}>
              <div className={`${type}-title`}>
                {header && (
                  <BodyText
                    type={
                      type === Profile.types.PROFILE
                        ? BodyText.types.STATIC16
                        : BodyText.types.CONTACTCARD
                    }
                    as={
                      type === Profile.types.PROFILE
                        ? 'p'
                        : getSEOWeight(seoWeight, seoWeight)
                    }
                  >
                    {header}
                  </BodyText>
                )}
              </div>
              <div className={`${type}-text`}>
                {bodyText && (
                  <BodyText
                    type={
                      type === Profile.types.PROFILE
                        ? BodyText.types.HEADER2TEXTGROUP
                        : BodyText.types.STATIC16
                    }
                  >
                    {bodyText}
                  </BodyText>
                )}
              </div>
              {type === Profile.types.CONTACT && (
                <div className="contact-info">
                  <BodyText
                    className="info-element name-text"
                    type={BodyText.types.CONTACTCARDBOLD}
                  >
                    {name}
                  </BodyText>

                  <BodyText
                    className="info-element"
                    type={BodyText.types.HEADER2TEXTGROUP}
                  >
                    {jobTitle}
                  </BodyText>

                  <BodyText
                    className="info-element contact-element"
                    type={BodyText.types.HEADER2TEXTGROUP}
                    {...emailProps}
                  >
                    {email}
                  </BodyText>

                  <BodyText
                    className="info-element contact-element"
                    type={BodyText.types.HEADER2TEXTGROUP}
                    {...phoneProps}
                  >
                    {formattedPhone}
                  </BodyText>
                </div>
              )}
            </div>
          )}
        />
      </div>
    );
  }
}

export default Profile;
